import { ElementRef, NgZone } from '@angular/core';
import { PlaybackService, PlaybackSession, Media, AssetResolution, PlayState, PlaybackHints, PlaybackItem } from 'media-playback';
import { Observable } from 'rxjs';
import { Subject, BehaviorSubject } from 'rxjs';
import { Logger } from '@tytapp/common';
import { Injectable } from '@angular/core';
import { AvAsset } from 'models';

/**
 * YouTubePlayer API
 */
interface YouTubePlayer {
    getCurrentTime();
    pauseVideo();
    playVideo();
    stopVideo();
    seekTo(positionSeconds : number, allowLookAhead? : boolean);
    mute();
    unMute();
    getVolume();
    setVolume(levelPercent : number);
    getDuration() : number;
    getAvailablePlaybackRates(): number[];
    getPlaybackRate() : number;
    setPlaybackRate(suggestedRate : number);
}

/**
 * YouTube API
 */
interface YouTube {
    Player : { new(...args) : YouTubePlayer }
}

/**
 * The Loader
 */
class YouTubeLoader {
    private static src = 'https://www.youtube.com/iframe_api';

    private static loading: boolean = false;
    private static loaded: boolean = false;
    private static listeners: any[] = [];

    static load(callback : (YT : YouTube) => void) {
        var _this = this;
        this.listeners.push(callback);

        if(this.loaded) {
            setTimeout(function() {
                _this.done();
            });
            return;
        }

        if(this.loading) {
            return;
        }

        this.loading = true;

        if (typeof window === 'undefined')
            return;
            
        window['onYouTubeIframeAPIReady'] = function() {
            _this.loaded = true;
            _this.done();
        };

        var script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = this.src;
        document.body.appendChild(script);
    }

    static done() {
        delete window['onYouTubeIframeAPIReady'];

        while(this.listeners.length) {
            this.listeners.pop()(window['YT']);
        }
    }
};


/**
 * YouTube playback service
 */
@Injectable()
export class YouTubePlaybackService implements PlaybackService {

    constructor(
        private zone : NgZone
    ) {

    }

    get id() { return 'youtube'; }

    resolve(item : PlaybackItem) : AssetResolution {
        let asset : AvAsset = item.item.asset;
        let url = asset.url;

		url = url
			.replace(/^https?:\/\//, '')
			.replace(/^www\./, '')
			.replace(/^youtu.be\//, 'youtube.com')
		;

		if (!url.startsWith('youtube.com/watch?v='))
            return null;

        let params = {};
        
        url.replace(/^.*\?/, '').split('&').map(x => x.split('=', 2)).forEach(pair => {
            params[pair[0]] = decodeURIComponent(pair[1]);
        });
        
        let id = params['v'];
        if (!id)
            return null;

        params['id'] = params['v'];

        return {
            item,
            service: this, 
            asset,
            data: params
        };
    }

    private styles = `
        .youtube-player {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            width: 100%;
            height: 100%;
        }

        .youtube-player iframe {
            height: 100%;
            position: absolute;
            width: 100%;
        }
    `;

    private styleElement : HTMLElement = null;

    /**
     * Play the given media resolution within the given root element.
     */
    play(mediaResolution : AssetResolution, rootElement : ElementRef, playbackHints : PlaybackHints) : Promise<PlaybackSession> {

        // Ensure the global CSS styles are installed.

        if (typeof document !== 'undefined') {
            let styleEl = this.styleElement;

            if (!styleEl) {
                styleEl = document.getElementById(`youtube-styles`);
                if (!styleEl) {
                    styleEl = document.createElement('style');
                    styleEl.innerHTML = this.styles;
                    styleEl.id = 'youtube-styles';
                    document.head.appendChild(styleEl);
                }

                this.styleElement = styleEl;
            }
        }

        // Construct a new element to hold the YouTube iframe

        let element = <HTMLElement>rootElement.nativeElement;
		let playerId = 'ytplayer_'+Math.floor(Math.random()*10000);
        let id = mediaResolution.data['id'];

        element.innerHTML = `<div class="youtube-player"><div id="${playerId}"></div></div>`;

        // Initialize the player and wait for it to be ready...

        let autoplay = playbackHints.autoplay !== false;

        return new Promise<PlaybackSession>((resolve, reject) => {
            let session : YouTubePlaybackSession = null;

            YouTubeLoader.load(YT => {
                let player = new YT.Player(playerId, {
                    height: '100%',
                    width: '100%',
                    videoId: id,
                    playerVars: {
                        controls: 0,
                        autoplay: 0,
                        playsinline: 1,
                        origin: document['origin'],
                        cc_load_policy: 0,
                        showinfo: 0
                    },
                    events: {
                        onReady: () => {
                            // Player is ready!
                            Logger.importantSuccess(`YouTube player is ready!`);
                            Logger.inspect('YouTube Player', player);

                            if (!autoplay)
                                player.playVideo();

                            session = new YouTubePlaybackSession(
                                this, mediaResolution, element.querySelector('.youtube-player'), 
                                player, this.zone, playbackHints && playbackHints.isLive? true : false
                            );

                            if (playbackHints && playbackHints.startPosition)
                                session.seek(playbackHints.startPosition);
                                
                            resolve(session);
                        },
                        onStateChange: event => session.handlePlayStateChanged(event.data),
                        onError: ev => {
                            Logger.criticalFailure(`YouTube has failed to start`);
                            Logger.inspect('YouTube Error Details', ev);

                            reject(new Error('Error occurred while preparing YouTube player: '));
                        },
                        onPlaybackRateChange: ev => this.zone.runGuarded(() => session.onPlaybackRateChange(ev))
                    }
                });

            });
        })
    }
}

/**
 * YouTube playback session (used by YouTubePlaybackService)
 */
export class YouTubePlaybackSession implements PlaybackSession {

    constructor(
        service : YouTubePlaybackService, 
        private mediaResolution : AssetResolution, 
        element : HTMLElement, 
        ytPlayer : YouTubePlayer,
        private zone : NgZone,
        private isLive : boolean
    ) {
        this._service = service;
        this._asset = mediaResolution.asset;
        this._resolution = mediaResolution;
        this._element = element;
        this._ytPlayer = ytPlayer;

        let firstUpdate = true;

        window['yt'] = ytPlayer;
        this.fullDuration = 0;
        let loadStamp = 0;

        this.supportedPlaybackSpeeds = ytPlayer.getAvailablePlaybackRates();
        
        this._positionInterval = setInterval(() => {

            if (this.isLive) {

                let newDuration = this.fullDuration;
                let position = this.position;

                if (loadStamp > 0)
                    newDuration += (Date.now() - loadStamp) / 1000.0;

                if (newDuration > 0 && position > 0 && loadStamp === 0) {
                    loadStamp = Date.now();
                    this.fullDuration = position;
                }

                if (position > this.fullDuration) {
                    this.fullDuration = position;
                    loadStamp = Date.now();
                }


                let reportedDuration = Math.min(this.dvrLimit, this.fullDuration);
                let unseekableOffset = this.fullDuration - reportedDuration;
                let reportedPosition = position - unseekableOffset;

                this._positionChanged.next(reportedPosition);

                if (this._length != reportedDuration) {
                    this._length = reportedDuration;
                    this._lengthChanged.next(this._length);
                }

                // if (this.isLive && newDuration > 0 && this.position > 0 && firstUpdate) {
                //     this.liveOffset = newDuration - this.position + (newDuration * 0.02);
                //     firstUpdate = false;
                // }
            } else {
                this._positionChanged.next(this.position);
                let newDuration = ytPlayer.getDuration();
                
                if (this._length != newDuration) {
                    this._length = newDuration;
                    this._lengthChanged.next(Math.min(newDuration, this._length));
                }
            }

            let newSpeed = ytPlayer.getPlaybackRate();
            if (this._speed != newSpeed) {
                this._speed = newSpeed;
                this._speedChanged.next(this.speed);
            }

        }, 1000);

        this._length = ytPlayer.getDuration();
        this._lengthChanged.next(this._length);

        //if (mediaResolution.data['seekable'] !== undefined)
        //    this._seekable = mediaResolution.data['seekable'] == '1';
    }

    fullDuration : number = 0;
    supportedPlaybackSpeeds : number[];
    _speed : number;
    _speedChanged : BehaviorSubject<number> = new BehaviorSubject<number>(1);

    setSpeed(rate : number) {
        this.ytPlayer.setPlaybackRate(rate);
    }
    
    onPlaybackRateChange(ev) {
        if (this._speed != ev.data) {
            this._speed = ev.data;
            this._speedChanged.next(this._speed);
        }
    }

    get speedChanged(): Observable<number> {
        return this._speedChanged;
    }

    get speed() {
        return this.ytPlayer.getPlaybackRate();
    }

    get item() {
        return this.mediaResolution.item;
    }
    
    get dvrLimit() {
        return 60 * 60 * 4;
    }

    seek(positionInSeconds : number) {
        if (this.isLive) {
            let reportedDuration = Math.min(this.dvrLimit, this.fullDuration);
            let unseekableOffset = this.fullDuration - reportedDuration;

            this.ytPlayer.seekTo(unseekableOffset + positionInSeconds);
        } else {
            this.ytPlayer.seekTo(positionInSeconds);
        }

        this._onSeek.next(positionInSeconds);
    }

    liveOffset : number = 0;

    get popOutURL() {
        return this.mediaResolution.url;
    }
    
    get volume() {
        return this.ytPlayer.getVolume() / 100.0;
    }

    handlePlayStateChanged(state) {
        this.zone.run(() => {
            if (state == 0) {
                // Ended
                this.onEnd();
            } else if (state == 1) {
                this._playState = 'playing';
                this._playStateChanged.next('playing');
            } else if (state == 2) {
                this._playState = 'paused';
                this._playStateChanged.next('paused');
            } else if (state == 3) {
                this._playState = 'buffering';
                this._playStateChanged.next('buffering');
            }
        })
    }

    onEnd() {
        this._finished.next();
    }
    
    stop() {
        this.ytPlayer.stopVideo();
    }

    destroy() {
        try {
            this.stop();
        } catch (e) {
            console.error(`YouTube: Caught error while trying to destroy video:`);
            console.error(e);
        }

        this._element.remove();
        clearInterval(this._positionInterval);
    }

    private _positionInterval;
    private _service : YouTubePlaybackService;
    private _asset : AvAsset;
    private _resolution : AssetResolution;
    private _element : HTMLElement;
    private _length : number;
    private _position : number;
    private _ytPlayer : YouTubePlayer;
    private _positionChanged : Subject<number> = new Subject<number>();
    private _lengthChanged : BehaviorSubject<number> = new BehaviorSubject<number>(0);
    private _finished : Subject<void> = new Subject<void>();
    private _playStateChanged : Subject<PlayState> = new Subject<PlayState>();
    private _seekable : boolean = true;
    private _onSeek : Subject<number> = new Subject<number>();

    get onSeek() : Observable<number> {
        return this._onSeek;
    }

    get finished() : Observable<void> {
        return this._finished;
    }

    get playStateChanged() : Observable<PlayState> {
        return this._playStateChanged;
    }

    get seekable() {
        return this._seekable;
    }

    get hasChat() {
        return true;
    }

    get chatEmbed() {
        let origin = 'tytnetwork.com';
        if (typeof window !== 'undefined')
            origin = window.location.hostname;

        let chatUrl = `https://www.youtube.com/live_chat?v=${this._resolution.data.id}&is_popout=1&embed_domain=${origin}&app=desktop`;
        return `
            <iframe style="width:100%; height:100%; top: 0; left: 0; right: 0; bottom: 0; position: absolute; border: none;" 
                src="${chatUrl}"></iframe>`
        ;
    }

    get length() { return this._length - this.liveOffset; }
    get position() { return this.ytPlayer.getCurrentTime(); }
    get service() { return this._service; }
    get element() { return { nativeElement: this._element }; }
    get asset() { return this._asset; }
    get positionChanged() : Observable<number> { return this._positionChanged; }
    get lengthChanged() : Observable<number> { return this._lengthChanged; }
    get playState() { return this._playState; }
    _playState : PlayState;
    
    private get ytPlayer() {
        return this._ytPlayer;
    }

    async pause() { 
        try {
            await this.ytPlayer.pauseVideo();
        } catch (e) {

        }
    }

    async resume() { 
        try {
            await this.ytPlayer.playVideo();
        } catch (e) {

        }
    }

    setVolume(level : number) { 
        this.ytPlayer.setVolume(level * 100);
        this._volumeChanged.next(level);
    }

    private _volumeChanged : Subject<number> = new Subject<number>();
    get volumeChanged() : Observable<number> {
        return this._volumeChanged;
    }
}
